import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SettingsService } from '@app/_services/settings.service';
import { CacheService } from '@app/_services/cache.service';
import { ModalService } from '@app/_services/modal.service';
import { BRAND_LOGO } from '@app/_utils/brand-icons';
import { MeoStore } from '@app/_stores/meo.store';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { LongPressDirective } from '@app/directives/long-press.directive';
import { MeoAiMemoryComponent } from '../meo-ai-memory/meo-ai-memory.component';

@Component({
  selector: 'app-meo-history',
  standalone: true,
  imports: [
    CommonModule,
    SvgComponent,
    LongPressDirective,
  ],
  templateUrl: './meo-history.component.html',
  styleUrl: './meo-history.component.scss',
})
export class MeoHistoryComponent implements OnInit {
  readonly ss = inject(SettingsService);
  readonly cc = inject(CacheService);
  readonly meoStore = inject(MeoStore);
  readonly brandLogo = BRAND_LOGO;
  private readonly modalService = inject(ModalService);

  ngOnInit(): void {
    // Bootstrap: read sessions directly from IndexedDB.
    // The reactive pipeline (withHooks) handles live updates from RTDB sync,
    // but this direct read ensures data is available immediately on component mount.
    this.meoStore.loadSessions();
  }

  onSelectSession(sessionId: string): void {
    this.meoStore.switchSession(sessionId);
    this.meoStore.openPanel();
  }

  onNewChat(): void {
    this.meoStore.startNewSession();
    this.meoStore.openPanel();
  }

  onNewChatLongPress(): void {
    this.meoStore.startNewSession();
    this.meoStore.openPanel(true); // autoStartMic = true
  }

  onClose(): void {
    window.history.back();
  }

  onOpenAiMemory(): void {
    this.modalService.openLevel2(MeoAiMemoryComponent);
  }

  onLoadMore(): void {
    if (this.meoStore.hasMoreLocalSessions()) {
      this.meoStore.loadMoreSessions();
    } else if (this.meoStore.hasMoreCloudSessions()) {
      this.meoStore.loadMoreFromCloud();
    }
  }

  getRelativeTime(date: Date): string {
    const now = Date.now();
    const then = new Date(date).getTime();
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return this.ss.t('meo_time_just_now');
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  }

  getConfirmedCount(session: { turns: { role: string; suggestions: { outcome: string }[] }[] }): number {
    let count = 0;
    for (const turn of session.turns) {
      if (turn.role === 'assistant') {
        for (const s of turn.suggestions) {
          if (s.outcome === 'confirmed') count++;
        }
      }
    }
    return count;
  }

  getSuggestionCount(session: { turns: { role: string; suggestions: { outcome: string }[] }[] }): number {
    let count = 0;
    for (const turn of session.turns) {
      if (turn.role === 'assistant') {
        count += turn.suggestions.length;
      }
    }
    return count;
  }
}
